import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Loader2 } from "lucide-react";
import { SecondaryButton } from "@/components/roava";
import { recordManualGpsNow } from "@/lib/native/manual-gps";

/**
 * One-shot "Record GPS now" button.
 *
 * Shared between the Offline Controls page and the Trip home tab so users
 * can capture a manual fix without digging into Offline Controls.
 * Behavior, labels, and toast copy are intentionally identical to the
 * original inline version that lived in `offline-control.tsx`.
 */
export function ManualGpsButton({
  tripId,
  className = "mt-3 h-12 w-full rounded-2xl text-sm font-medium",
  onSuccess,
}: {
  tripId: string;
  className?: string;
  /** Fires after a successful save (sent or queued). Use to refetch counters. */
  onSuccess?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const abortRef = useRef<{ aborted: boolean } | null>(null);


  useEffect(() => {
    if (!busy) { setElapsed(0); return; }
    const startedAt = Date.now();
    const id = window.setInterval(() => setElapsed(Date.now() - startedAt), 500);
    return () => clearInterval(id);
  }, [busy]);

  async function onTap() {
    if (busy) {
      // Second tap = cancel. The underlying CoreLocation request can't
      // truly be cancelled, but we release the UI so the user isn't stuck.
      if (abortRef.current) abortRef.current.aborted = true;
      setBusy(false);
      toast("Cancelled.");
      return;
    }
    const token = { aborted: false };
    abortRef.current = token;
    setBusy(true);
    try {
      const r = await recordManualGpsNow(tripId);
      if (token.aborted) return;
      if (r.ok) {
        if (r.source === "manual_gps_recent" && r.ageMs != null) {
          const secs = Math.max(1, Math.round(r.ageMs / 1000));
          toast.success(
            r.status === "queued"
              ? `Saved last fix from ${secs}s ago. It will sync when you're back online.`
              : `Saved last fix from ${secs}s ago.`,
          );
        } else if (r.source === "manual_gps_low_accuracy") {
          toast.success(
            r.status === "queued"
              ? "Saved a low-accuracy fix (network-based). It will sync when you're back online."
              : "Saved a low-accuracy fix (network-based).",
          );
        } else {
          toast.success(r.status === "queued"
            ? "Current location saved. It will sync when you're back online."
            : "Current location saved.");
        }
        try { onSuccess?.(); } catch { /* ignore */ }
      } else if (r.reason === "weak-fix") {
        toast.error("GPS fix too weak — try near a window or open sky.");
        // Intentionally do NOT call onSuccess — nothing was saved.
      } else if (r.reason === "timeout") {
        toast.error("No GPS fix — try near a window or with Wi-Fi on, then tap again.");
      } else {
        toast.error("Couldn't get a fresh GPS fix. Try again with a clearer view of the sky.");
      }

    } finally {
      if (!token.aborted) setBusy(false);
    }
  }

  const label = !busy
    ? "Record GPS now"
    : elapsed >= 10_000
      ? "Still trying… tap to cancel"
      : elapsed >= 4_000
        ? "Locating… GPS can be slow indoors"
        : "Recording…";

  return (
    <SecondaryButton onClick={onTap} className={className}>
      {busy
        ? <><Loader2 className="size-4 mr-2 animate-spin" />{label}</>
        : <><Crosshair className="size-4 mr-2" />{label}</>}
    </SecondaryButton>
  );
}

export default ManualGpsButton;
